import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Table,
  Row,
  Col,
  message,
  Modal,
  Descriptions,
  Card,
  Pagination,
  Typography,
} from "antd";
import { useMediaQuery } from "react-responsive";
import { DeleteOutlined } from "@ant-design/icons";
import UserCreateForm from "../../page-components/UserManagement/UserCreateForm";
import userManagementService from "../../services/userManagementService";
import usePermissions from "../../hooks/usePermissions";
import SearchFilters from "./SearchFilters";
import { FilterOutlined } from "@ant-design/icons";
const { Paragraph } = Typography;

const UserManagement = () => {
  const { hasPermission } = usePermissions();
  const [modalVisible, setModalVisible] = useState(false);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination for mobile
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [filtersList, setFiltersList] = useState([]);

  const [showFilters, setShowFilters] = useState(false);
  const [locations, setLocations] = useState([]);
  // Mobile detection
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const toggleFilters = () => setShowFilters(!showFilters);
  useEffect(() => {
    userManagementService.getLocationsDropdown().then((res) => {
      if (res.success) {
        setLocations(res.data);
      } else {
        message.error("Failed to load locations");
      }
    });
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const res = await userManagementService.getUserList(
      pagination.current,
      pagination.pageSize,
      filters
    );
    setLoading(false);
    if (res.success) {
      console.log(res.data);
      setUserList(res.data.results);
      setFiltersList(res.data.filters);
      setPagination((prev) => ({ ...prev, total: res.data.count || 0 }));
    } else {
      message.error(res.error || "Failed to fetch users");
    }
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize, filters]);

  const handleDeleteUser = async (record) => {
    const res = await userManagementService.deactivateUser(record.uid);
    if (res.success) {
      message.success("User deactivated");
      fetchUsers();
    } else {
      message.error(res.error || "Failed to deactivate user");
    }
  };

  const handleActivateUser = async (record) => {
    const res = await userManagementService.activateUser(record.uid);
    if (res.success) {
      message.success("User activated");
      fetchUsers();
    } else {
      message.error(res.error || "Failed to activate user");
    }
  };

  const handleSearchClick = async (newFilters) => {
    setFilters(newFilters);
    setUserList([]);
    setPagination({ ...pagination, current: 1 });
  };
  const handleCancel = async () => {
    setFilters({});
    setUserList([]);

    // Fetch data with the reset filters
  };

  const handlePaginationChange = async (page, pageSize) => {
    // Update the pagination in parent state
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));

    // Trigger data loading with updated pagination
  };
  const columns = [
    { title: "Full Name", dataIndex: "name", key: "name" },
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "User Type", dataIndex: "user_type", key: "user_type" },
    {
      title: "Locations",
      dataIndex: "locations",
      key: "locations",
      render: (locs) => (Array.isArray(locs) ? locs.join(", ") : locs),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        if (record.is_active) {
          return hasPermission("can_deactivate_internal_user_account") ? (
            <span
              style={{
                color: "#1890ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => {
                Modal.confirm({
                  title: "Are you sure you want to deactivate this user?",
                  okText: "Yes",
                  cancelText: "No",
                  onOk: () => handleDeleteUser(record),
                });
              }}
            >
              Deactivate User
            </span>
          ) : (
            <span
              style={{
                color: "gray",
                cursor: "not-allowed",
                textDecoration: "none",
              }}
            >
              Deactivate User
            </span>
          );
        }

        // inactive user
        return hasPermission("can_activate_internal_user_account") ? (
          <span
            style={{
              color: "#1890ff",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => {
              Modal.confirm({
                title: "Are you sure you want to activate this user?",
                okText: "Yes",
                cancelText: "No",
                onOk: () => handleActivateUser(record),
              });
            }}
          >
            Activate User
          </span>
        ) : (
          <span style={{ color: "gray" }}>User Deactivated</span>
        );
      },
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        boxSizing: "border-box",
        maxWidth: "100vw",
      }}>
      {showFilters && (
        <SearchFilters
          filtersList={filtersList}
          onSearch={handleSearchClick}
          fetchData={fetchUsers}
          setUserList={setUserList}
          onCancel={handleCancel}
          loading={loading}
          locations={locations}
        />
      )}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16, flexWrap: "nowrap", width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "flex-end" : "space-between",
            alignItems: isMobile ? "flex-end" : "center",
            marginBottom: "10px",
            flexDirection: isMobile ? "column-reverse" : "row",
            gap: isMobile ? 2 : 0,
            width: "100%",
          }}>
          <Paragraph style={{ fontWeight: 500, opacity: 0.6, margin: 0 }}>
            Showing{" "}
            {Math.min(
              (pagination.current - 1) * pagination.pageSize + 1,
              pagination.total
            )}{" "}
            -{" "}
            {Math.min(
              pagination.current * pagination.pageSize,
              pagination.total
            )}{" "}
            of {pagination.total} users
          </Paragraph>

          <div style={{ display: "flex", gap: "10px", marginTop: 10 }}>
            {/* {activeTab === "Transactions" && (
                        <Button
                          type="primary"
                          onClick={handleDownloadCsv}
                          style={{ marginLeft: "10px", background: "#040724" }}>
                          Download CSV
                        </Button>
                      ) */}

            <Button
              type="primary"
              style={{ background: "#001529" }}
              icon={<FilterOutlined />}
              disabled={loading}
              onClick={toggleFilters}
            />
            {hasPermission("can_create_internal_user_account") && (
              <Button
                style={{ backgroundColor: "#040724", minWidth: 120 }}
                type="primary"
                onClick={() => setModalVisible(true)}>
                Create User
              </Button>
            )}
          </div>
        </div>
      </Row>
      <UserCreateForm
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={() => {
          setModalVisible(false);
          fetchUsers();
        }}
        locations={locations}
        setLocations={setLocations}
      />

      {/* Desktop/tablet: Table view */}
      {!isMobile && (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}>
          <Table
            columns={columns}
            dataSource={userList}
            rowKey={(record) => record.id || record.username}
            loading={loading}
            bordered
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize });
              },
              onShowSizeChange: (current, size) => {
                setPagination({ ...pagination, current: 1, pageSize: size });
              },
            }}
            style={{ minWidth: 600 }}
          />
        </div>
      )}

      {/* Mobile: Card/Descriptions view */}
      {isMobile && (
        <div>
          {userList
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((user, idx) => (
              <Card
                key={user.id || user.username}
                style={{
                  marginBottom: 8,
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                  boxShadow: "0 1px 2px #f0f1f2",
                }}
                bodyStyle={{ paddingBottom: 0 }}>
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Full Name">
                    {user.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Username">
                    {user.username}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {user.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="User Type">
                    {user.user_type}
                  </Descriptions.Item>
                  <Descriptions.Item label="Locations">
                    {Array.isArray(user.locations)
                      ? user.locations.join(", ")
                      : user.locations}
                  </Descriptions.Item>
                </Descriptions>
                <div
                  style={{
                    borderTop: "1px solid #f0f0f0",
                    padding: 8,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}>
                  {user.is_active ? (
                    hasPermission("can_deactivate_internal_user_account") ? (
                      <span
                        style={{
                          color: "#1890ff",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => {
                          Modal.confirm({
                            title:
                              "Are you sure you want to deactivate this user?",
                            okText: "Yes",
                            cancelText: "No",
                            onOk: () => handleDeleteUser(user),
                          });
                        }}>
                        Deactivate User
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "gray",
                          cursor: "not-allowed",
                          textDecoration: "none",
                        }}>
                        Deactivate User
                      </span>
                    )
                  ) : hasPermission("can_activate_internal_user_account") ? (
                    <span
                      style={{
                        color: "#1890ff",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        Modal.confirm({
                          title: "Are you sure you want to activate this user?",
                          okText: "Yes",
                          cancelText: "No",
                          onOk: () => handleActivateUser(user),
                        });
                      }}
                    >
                      Activate User
                    </span>
                  ) : (
                    <span style={{ color: "gray" }}>User Deactivated</span>
                  )}
                </div>
              </Card>
            ))}
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePaginationChange}
              size="small"
              showSizeChanger={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
